import CharacterCount from '@tiptap/extension-character-count';
import Dropcursor from '@tiptap/extension-dropcursor';
import { Highlight } from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { type Extensions, Mark } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { ContentBlockquote } from './custom-extensions/content-block-quote';
import { ContentBulletList } from './custom-extensions/content-bullet-list';
import { ContentCodeBlock } from './custom-extensions/content-code-block';
import { ContentHeading } from './custom-extensions/content-heading';
import { ContentImage } from './custom-extensions/content-image';
import { createContentLink } from './custom-extensions/content-link';
import { ContentListItem } from './custom-extensions/content-list-item';
import { ContentOrdererdList } from './custom-extensions/content-ordered-list';
import { ContentParagraph } from './custom-extensions/content-paragraph';
import {
  ContentTable,
  ContentTableCell,
  ContentTableHeader,
} from './custom-extensions/content-table';
import { DefaultHeading } from './custom-extensions/default-heading';

// Common two-part TLDs that should be treated as a single TLD
const twoPartTlds = [
  'co.uk',
  'com.au',
  'co.nz',
  'co.za',
  'com.br',
  'com.mx',
  'com.ar',
  'co.jp',
  'com.cn',
  'com.tw',
  'com.hk',
  'com.sg',
  'co.in',
  'com.tr',
  'com.eg',
  'co.ke',
  'com.ng',
  'com.za',
  'co.il',
  'com.ua',
  'com.pl',
  'com.ro',
  'com.gr',
  'com.pt',
  'com.es',
  'com.it',
  'com.fr',
  'com.de',
  'com.nl',
  'com.be',
  'com.ch',
  'com.at',
  'com.se',
  'com.no',
  'com.dk',
  'com.fi',
  'com.ie',
  'com.my',
  'com.ph',
  'com.id',
  'com.vn',
  'com.th',
  'com.kr',
  'com.jp',
  'com.cn',
  'org.uk',
  'net.au',
  'gov.uk',
  'ac.uk',
  'edu.au',
  'gov.au',
  'gouv.fr',
];

/**
 * Extracts the root domain from a hostname.
 * Handles common two-part TLDs like .co.uk, .com.au, etc.
 * Examples:
 * - example.com -> example.com
 * - www.example.com -> example.com
 * - blog.example.com -> example.com
 * - example.co.uk -> example.co.uk
 * - www.example.co.uk -> example.co.uk
 */
function getRootDomain(hostname: string): string | null {
  if (hostname === '' || hostname.length === 0) {
    return null;
  }

  // Remove port if present
  const hostnameWithoutPort = hostname.split(':')[0];
  const parts = hostnameWithoutPort.split('.');

  if (parts.length === 0) {
    return null;
  }

  // Check if the last two parts form a known two-part TLD
  if (parts.length >= 2) {
    const lastTwoParts = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    if (twoPartTlds.includes(lastTwoParts.toLowerCase())) {
      // For two-part TLDs, we need at least 3 parts (subdomain.domain.tld)
      if (parts.length >= 3) {
        return `${parts[parts.length - 3]}.${lastTwoParts}`;
      }
      // If only 2 parts, return as is (e.g., example.co.uk)
      return hostnameWithoutPort;
    }
  }

  // For standard TLDs, take the last two parts (domain.tld)
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }

  // If only one part, return as is (though this is unusual)
  return hostnameWithoutPort;
}

/**
 * Extracts hostname from a URL string.
 * Handles various formats: full URLs, protocol-relative URLs, and bare domains.
 */
function extractHostname(urlString: string): string | null {
  if (urlString.length === 0 || urlString.trim().length === 0) {
    return null;
  }

  const trimmed = urlString.trim();

  // Protocol-relative URL (//example.com)
  if (trimmed.startsWith('//')) {
    try {
      const url = new URL(trimmed, 'https://placeholder');
      return url.hostname;
    } catch {
      // If URL parsing fails, try to extract manually
      const withoutProtocol = trimmed.slice(2).split('/')[0].split('?')[0].split('#')[0];
      return withoutProtocol.length > 0 ? withoutProtocol : null;
    }
  }

  // Full URL with protocol
  if (trimmed.includes('://')) {
    try {
      const url = new URL(trimmed);
      return url.hostname;
    } catch {
      return null;
    }
  }

  // Bare domain or domain with path (e.g., "example.com" or "example.com/path")
  // Extract the domain part (everything before the first /, ?, or #)
  const domainPart = trimmed.split('/')[0].split('?')[0].split('#')[0];

  // Validate if it looks like a domain (contains at least one dot or is a valid hostname)
  if (domainPart.includes('.') || /^[a-zA-Z0-9-]+$/.test(domainPart)) {
    return domainPart;
  }

  return null;
}

/**
 * Checks if a URL is external to the given domain (matches backend logic)
 * @param href - The URL to check
 * @param baseWebsite - The base website/domain to compare against (e.g., 'example.com')
 * @returns true if the link is external, false if internal
 */
export function isExternalLink(
  href: string | null | undefined,
  baseWebsite?: string | null
): boolean {
  // if we don't have a website, we always want to treat it as an external
  if (baseWebsite == null || baseWebsite.length === 0) {
    return true;
  }

  if (href == null || href === undefined) {
    return true;
  }

  // this is also considered local
  if (href.trim().indexOf('/') === 0) {
    return false;
  }

  // anchor links (starting with #) are always considered local
  if (href.trim().indexOf('#') === 0) {
    return false;
  }

  // Extract hostnames from both href and baseWebsite
  const hrefHostname = extractHostname(href);
  const baseHostname = extractHostname(baseWebsite);

  // If we can't extract hostnames, fall back to substring matching
  if (hrefHostname == null || baseHostname == null) {
    return href.trim().indexOf(baseWebsite.trim()) === -1;
  }

  // Get root domains from both hostnames
  const hrefRootDomain = getRootDomain(hrefHostname);
  const baseRootDomain = getRootDomain(baseHostname);

  // If we can't determine root domains, fall back to substring matching
  if (hrefRootDomain == null || baseRootDomain == null) {
    return href.trim().indexOf(baseWebsite.trim()) === -1;
  }

  // Compare root domains (case-insensitive)
  return hrefRootDomain.toLowerCase() !== baseRootDomain.toLowerCase();
}

// NOTE: if you change something here, you need to make sure that backend
//       is in sync
function createExtensions(
  website?: string | null,
  linkCallbacks?: {
    onEditLink?: (href: string, text: string) => void;
    onUnlinkLink?: () => void;
    onHoverStart?: () => void;
    onHoverEnd?: () => void;
  }
): Extensions {
  return [
    // Sync with backend
    StarterKit.configure({
      paragraph: false,
      heading: false,
      dropcursor: false,
      blockquote: false,
      codeBlock: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      history: false,
    }),
    ContentHeading.configure({
      levels: [1, 2, 3, 4, 5, 6],
    }),
    ContentParagraph,
    Mark.create({
      name: 'strong',
      renderHTML({ HTMLAttributes }) {
        return ['strong', HTMLAttributes, 0];
      },
      parseHTML() {
        return [
          {
            tag: 'strong',
          },
        ];
      },
    }),
    Mark.create({
      name: 'em',
      renderHTML({ HTMLAttributes }) {
        return ['em', HTMLAttributes, 0];
      },
      parseHTML() {
        return [
          {
            tag: 'em',
          },
        ];
      },
    }),
    Underline,
    linkCallbacks != null
      ? createContentLink(
          website,
          linkCallbacks.onEditLink,
          linkCallbacks.onUnlinkLink,
          linkCallbacks.onHoverStart,
          linkCallbacks.onHoverEnd
        )
      : Link.configure({
          openOnClick: false,
          defaultProtocol: 'https',
          protocols: ['http', 'https'],
          HTMLAttributes: {
            rel: null,
            target: null,
          },
        }).extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              target: {
                default: null,
                parseHTML: function (element) {
                  return element.getAttribute('target');
                },
                renderHTML: function (attributes) {
                  if (attributes.target == null) {
                    return {};
                  }
                  return {
                    target: attributes.target as string,
                  };
                },
              },
              rel: {
                default: null,
                parseHTML: function () {
                  return null;
                },
                renderHTML: function () {
                  return {};
                },
              },
            };
          },
          renderHTML({ HTMLAttributes }) {
            const href = (HTMLAttributes.href as string | null | undefined) ?? null;
            const isExternal = isExternalLink(href, website ?? '');

            const targetAttribute = (HTMLAttributes.target as string | null | undefined) ?? null;
            const target = targetAttribute ?? (isExternal ? '_blank' : null);

            return [
              'a',
              {
                ...HTMLAttributes,
                target,
                rel: null,
              },
              0,
            ];
          },
        }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    ContentBulletList,
    ContentListItem,
    ContentTable,
    TableRow,
    ContentTableHeader,
    ContentTableCell,
    ContentOrdererdList,
    ContentCodeBlock,
    ContentBlockquote,
    // Frontend only
    CharacterCount,
    Dropcursor.configure({
      // accent 600 color
      color: '#3e63dd',
      width: 1.5,
    }),
    DefaultHeading,
    Highlight.configure({
      multicolor: true,
    }),
    History,
  ];
}

export function getExtensions(
  onContentImageEdit: (nodePosition: number, src: string, alt?: string) => unknown,
  website?: string | null,
  linkCallbacks?: {
    onEditLink?: (href: string, text: string) => void;
    onUnlinkLink?: () => void;
    onHoverStart?: () => void;
    onHoverEnd?: () => void;
  }
) {
  return [
    ...createExtensions(website, linkCallbacks),
    ContentImage.configure({
      inline: true,
      allowBase64: true,
      onEdit: onContentImageEdit,
    }),
  ];
}
